import { useContext, useEffect, useState } from 'react';
import { UserContext } from '@adflow/utils';
import { supportsTemplateAnimations } from '../animation';
import { useStore } from './useStore';

export const TEMPLATE_BUILDER_ANIMATIONS_FEATURE = 'cm-template-builder-animations';

/** Cypress can force the flag on/off via sessionStorage before the app loads. */
export const TEMPLATE_ANIMATIONS_CYPRESS_OVERRIDE_KEY = 'cm-template-builder-animations';

type WindowWithCypress = Window & { Cypress?: unknown };

function isRunningInsideCypress(): boolean {
  return typeof window !== 'undefined' && Boolean((window as WindowWithCypress).Cypress);
}

function readCypressOverride(): boolean | null {
  if (!isRunningInsideCypress()) {
    return null;
  }

  const value = window.sessionStorage.getItem(TEMPLATE_ANIMATIONS_CYPRESS_OVERRIDE_KEY);
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }

  return null;
}

export function useTemplateAnimationsFeature(): boolean {
  const { user } = useContext(UserContext);
  const importSourceType = useStore((state) => state.importSourceType);
  const [flagEnabled, setFlagEnabled] = useState(() => readCypressOverride() ?? false);

  useEffect(() => {
    let cancelled = false;
    const cypressOverride = readCypressOverride();
    if (cypressOverride != null) {
      setFlagEnabled(cypressOverride);
      return;
    }

    const checkFeature = async () => {
      const hasFeature = (await user?.hasFeature(TEMPLATE_BUILDER_ANIMATIONS_FEATURE)) ?? false;
      if (!cancelled) {
        setFlagEnabled(hasFeature);
      }
    };
    checkFeature().catch((error) => {
      console.warn('Failed to check template builder animations feature', error);
      if (!cancelled) {
        setFlagEnabled(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  return flagEnabled && supportsTemplateAnimations(importSourceType);
}
